import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../../lib/api";
import { fmtKwp } from "../../lib/format";
import { CLASSIFICATIONS, resolveClass } from "../../lib/classification";
import { RISK_STATUSES } from "../../lib/risk";
import RiskBadge from "../../components/RiskBadge";
import { Filter, Download, Loader2, RefreshCw, Save, Bookmark, Trash2, Edit3, FileDown } from "lucide-react";
import ConfirmDialog from "../../components/ConfirmDialog";
import DateInput from "../../components/DateInput";

/**
 * Custom Reports — ad-hoc filter builder under Analytics.
 *
 * Users pick any combination of classification / risk / lifecycle stage /
 * developer / operator / panel / inverter / codes / date-range / SLA
 * flag, and get a matching project table + a summary bar (count,
 * combined kWp, combined CAPEX £, avg £/kWp). Not-Proceeding projects
 * are excluded by default (toggle to include them).
 */
const currency = (v) => `£${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

export default function CustomReports() {
  const [filters, setFilters] = useState({
    business_classification: [],
    risk_status: [],
    lifecycle_status: [],
    developer: [],
    operator: [],
    panel_manufacturer: [],
    inverter_manufacturer: [],
    unit_code_contains: "",
    property_code_contains: "",
    created_from: "",
    created_to: "",
    sla_overrun_only: false,
    include_not_proceeding: false,
  });
  const [stageCatalog, setStageCatalog] = useState([]);
  const [options, setOptions] = useState({
    developer: [], operator: [], panel_manufacturer: [], inverter_manufacturer: [],
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dashLoaded, setDashLoaded] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState("");
  const [saveDialog, setSaveDialog] = useState({ open: false, mode: "create", id: null, name: "", shared: false });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Load stage catalog + auto-derived option lists from /pm/dashboard.
  useEffect(() => {
    (async () => {
      try {
        const [rolesRes, dashRes] = await Promise.all([
          api.get("/pm/roles"),
          api.get("/pm/dashboard"),
        ]);
        setStageCatalog(rolesRes.data.statuses || []);
        const opts = { developer: new Set(), operator: new Set(),
                       panel_manufacturer: new Set(), inverter_manufacturer: new Set() };
        for (const list of Object.values(dashRes.data.projects_by_status || {})) {
          for (const p of list) {
            for (const f of Object.keys(opts)) {
              const v = (p[f] || "").trim();
              if (v) opts[f].add(v);
            }
          }
        }
        setOptions(Object.fromEntries(Object.entries(opts).map(
          ([k, s]) => [k, [...s].sort((a, b) => a.localeCompare(b))]
        )));
        setDashLoaded(true);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
    })();
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/analytics/report-templates");
      setTemplates(data.templates || []);
    } catch { /* silent — templates optional */ }
  };

  const openSaveDialog = () => setSaveDialog({ open: true, mode: "create", id: null, name: "", shared: false });
  const openRenameDialog = () => {
    const t = templates.find(x => x.id === activeTemplateId);
    if (!t) return;
    setSaveDialog({ open: true, mode: "rename", id: t.id, name: t.name, shared: !!t.shared });
  };
  const closeSaveDialog = () => setSaveDialog({ open: false, mode: "create", id: null, name: "", shared: false });

  const saveTemplate = async () => {
    const name = (saveDialog.name || "").trim();
    if (!name) {
      toast.error("Give the template a name");
      return;
    }
    try {
      if (saveDialog.mode === "rename" && saveDialog.id) {
        const { data } = await api.patch(`/analytics/report-templates/${saveDialog.id}`, {
          name, shared: saveDialog.shared,
        });
        toast.success(`Template renamed to "${data.name || name}"`);
      } else {
        const { data } = await api.post("/analytics/report-templates", {
          name, filters, shared: saveDialog.shared,
        });
        toast.success(`Template "${data.name}" saved${data.shared ? " (shared with team)" : ""}`);
        setActiveTemplateId(data.id);
      }
      closeSaveDialog();
      loadTemplates();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const applyTemplate = (tid) => {
    setActiveTemplateId(tid);
    if (!tid) return;
    const t = templates.find(x => x.id === tid);
    if (!t) return;
    // Merge stored filters over defaults so newly-added fields land with defaults.
    setFilters(prev => ({ ...prev, ...(t.filters || {}) }));
    toast.info(`Loaded template "${t.name}" — hit Run report`);
  };

  const deleteActiveTemplate = async () => {
    if (!activeTemplateId) return;
    setDeleteConfirm(true);
  };
  const doDeleteTemplate = async () => {
    setDeleteConfirm(false);
    try {
      await api.delete(`/analytics/report-templates/${activeTemplateId}`);
      toast.success("Template deleted");
      setActiveTemplateId("");
      loadTemplates();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/analytics/custom-report", filters);
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Report failed");
    } finally { setLoading(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.post("/analytics/custom-report.csv", filters, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `custom-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "CSV export failed");
    }
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.post("/analytics/custom-report.pdf", filters, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `board-pack-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Board-pack PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "PDF export failed");
    } finally { setPdfBusy(false); }
  };

  const resetFilters = () => {
    setFilters({
      business_classification: [], risk_status: [], lifecycle_status: [],
      developer: [], operator: [], panel_manufacturer: [], inverter_manufacturer: [],
      unit_code_contains: "", property_code_contains: "",
      created_from: "", created_to: "",
      sla_overrun_only: false, include_not_proceeding: false,
    });
    setResult(null);
  };

  const toggleArray = (field, value) => {
    setFilters(prev => {
      const set = new Set(prev[field] || []);
      if (set.has(value)) set.delete(value); else set.add(value);
      return { ...prev, [field]: [...set] };
    });
  };

  const activeFilterCount = useMemo(() => {
    let n = 0;
    for (const f of ["business_classification", "risk_status", "lifecycle_status",
                     "developer", "operator", "panel_manufacturer", "inverter_manufacturer"]) {
      n += (filters[f] || []).length;
    }
    if (filters.unit_code_contains) n++;
    if (filters.property_code_contains) n++;
    if (filters.created_from) n++;
    if (filters.created_to) n++;
    if (filters.sla_overrun_only) n++;
    if (filters.include_not_proceeding) n++;
    return n;
  }, [filters]);

  return (
    <div data-testid="custom-reports-page">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label mb-1">Analytics</div>
          <h2 className="font-display text-2xl font-bold">Custom Reports</h2>
          <p className="muted text-sm mt-1">
            Filter the portfolio on any combination of fields — see combined kWp and CAPEX at the top, then export.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Bookmark size={12} className="muted" />
            <select
              className="input-box text-xs !py-1"
              value={activeTemplateId}
              onChange={(e) => applyTemplate(e.target.value)}
              data-testid="cr-template-select"
            >
              <option value="">— Load template —</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.shared ? " · shared" : ""}{!t.is_owner ? ` (by ${t.user_name || t.user_email})` : ""}
                </option>
              ))}
            </select>
            {activeTemplateId && templates.find(t => t.id === activeTemplateId)?.is_owner && (
              <>
                <button
                  type="button"
                  className="btn-ghost !py-1 !px-1.5 text-xs"
                  onClick={openRenameDialog}
                  title="Rename this template"
                  data-testid="cr-template-rename"
                >
                  <Edit3 size={11} />
                </button>
                <button
                  type="button"
                  className="btn-ghost !py-1 !px-1.5 text-xs"
                  onClick={deleteActiveTemplate}
                  title="Delete this template"
                  data-testid="cr-template-delete"
                >
                  <Trash2 size={11} />
                </button>
              </>
            )}
          </div>
          <button className="btn-ghost text-xs" onClick={openSaveDialog} data-testid="cr-template-save">
            <Save size={12} /> Save as…
          </button>
          {activeFilterCount > 0 && (
            <button className="btn-ghost text-xs" onClick={resetFilters} data-testid="cr-reset">
              <RefreshCw size={12} /> Reset
            </button>
          )}
          <button
            className="btn-accent text-xs"
            onClick={run}
            disabled={loading || !dashLoaded}
            data-testid="cr-run"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Filter size={12} />}
            {loading ? "Running…" : "Run report"}
          </button>
          <button
            className="btn-ghost text-xs"
            onClick={exportCsv}
            disabled={loading || !result}
            data-testid="cr-csv"
          >
            <Download size={12} /> CSV
          </button>
          <button
            className="btn-ghost text-xs"
            onClick={exportPdf}
            disabled={loading || !result || pdfBusy}
            title="Export as branded board-pack PDF"
            data-testid="cr-pdf"
          >
            {pdfBusy ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
            {pdfBusy ? "Building…" : "Board-pack PDF"}
          </button>
        </div>
      </div>

      {/* ============ Filter card ============ */}
      <div className="border border-[var(--line)] rounded-lg bg-white p-5 mb-6 space-y-5" data-testid="cr-filter-card">
        {/* Row 1: chip groups */}
        <ChipGroup
          label="Business classification"
          testId="cr-class"
          options={CLASSIFICATIONS.map(c => ({ value: c.key, label: c.label, tone: c.tone }))}
          selected={filters.business_classification}
          onToggle={(v) => toggleArray("business_classification", v)}
        />
        <ChipGroup
          label="Risk (RAG)"
          testId="cr-risk"
          options={RISK_STATUSES.map(r => ({ value: r.key, label: r.label, tone: r.tone }))}
          selected={filters.risk_status}
          onToggle={(v) => toggleArray("risk_status", v)}
        />
        <ChipGroup
          label="Lifecycle stage"
          testId="cr-lifecycle"
          options={stageCatalog.map(s => ({ value: s.key, label: s.label }))}
          selected={filters.lifecycle_status}
          onToggle={(v) => toggleArray("lifecycle_status", v)}
        />

        {/* Row 2: multi-select dropdowns for portfolio meta */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <MultiSelectMini label="Developer" testId="cr-developer"
            options={options.developer} selected={filters.developer}
            onToggle={(v) => toggleArray("developer", v)} />
          <MultiSelectMini label="Operator" testId="cr-operator"
            options={options.operator} selected={filters.operator}
            onToggle={(v) => toggleArray("operator", v)} />
          <MultiSelectMini label="Panel Mfr" testId="cr-panel-mfr"
            options={options.panel_manufacturer} selected={filters.panel_manufacturer}
            onToggle={(v) => toggleArray("panel_manufacturer", v)} />
          <MultiSelectMini label="Inverter Mfr" testId="cr-inverter-mfr"
            options={options.inverter_manufacturer} selected={filters.inverter_manufacturer}
            onToggle={(v) => toggleArray("inverter_manufacturer", v)} />
        </div>

        {/* Row 3: text + date + toggles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <div className="label !text-[10px] mb-1">Unit code contains</div>
            <input className="input-swiss" placeholder="e.g. U-24"
                   value={filters.unit_code_contains}
                   onChange={(e) => setFilters(f => ({ ...f, unit_code_contains: e.target.value }))}
                   data-testid="cr-unit-code" />
          </div>
          <div>
            <div className="label !text-[10px] mb-1">Property code contains</div>
            <input className="input-swiss" placeholder="e.g. PC-01"
                   value={filters.property_code_contains}
                   onChange={(e) => setFilters(f => ({ ...f, property_code_contains: e.target.value }))}
                   data-testid="cr-property-code" />
          </div>
          <div>
            <div className="label !text-[10px] mb-1">Created from</div>
            <DateInput className="input-box"
                   value={filters.created_from}
                   onChange={(e) => setFilters(f => ({ ...f, created_from: e.target.value }))}
                   data-testid="cr-date-from" />
          </div>
          <div>
            <div className="label !text-[10px] mb-1">Created to</div>
            <DateInput className="input-box"
                   value={filters.created_to}
                   onChange={(e) => setFilters(f => ({ ...f, created_to: e.target.value }))}
                   data-testid="cr-date-to" />
          </div>
        </div>

        <div className="flex items-center gap-6 flex-wrap">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={filters.sla_overrun_only}
                   onChange={(e) => setFilters(f => ({ ...f, sla_overrun_only: e.target.checked }))}
                   data-testid="cr-sla" />
            <span>SLA overrun only</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={filters.include_not_proceeding}
                   onChange={(e) => setFilters(f => ({ ...f, include_not_proceeding: e.target.checked }))}
                   data-testid="cr-include-np" />
            <span>Include Not-Proceeding projects</span>
            <span className="text-[10px] muted">(off by default — matches installed/pipeline totals)</span>
          </label>
        </div>
      </div>

      {/* ============ Summary bar + Results table ============ */}
      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="cr-summary">
            <SummaryCard label="Projects" value={result.count} testId="cr-summary-count" />
            <SummaryCard label="Combined kWp" value={fmtKwp(result.total_kwp)} accent testId="cr-summary-kwp" />
            <SummaryCard label="Combined CAPEX" value={currency(result.total_capex_gbp)} testId="cr-summary-capex" />
            <SummaryCard label="Avg £ per kWp" value={currency(result.avg_gbp_per_kwp)} testId="cr-summary-avg" />
          </div>

          <div className="border border-[var(--line)] rounded-lg bg-white overflow-x-auto" data-testid="cr-results">
            {result.rows.length === 0 ? (
              <div className="p-6 text-sm muted text-center" data-testid="cr-empty">
                No projects match this filter set. Loosen the filters or clear a chip above.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-[var(--line)] bg-[var(--surface-2,#F6F7F9)]">
                    <th className="px-4 py-2 label !text-[9px]">Project</th>
                    <th className="px-4 py-2 label !text-[9px]">Class</th>
                    <th className="px-4 py-2 label !text-[9px]">Risk</th>
                    <th className="px-4 py-2 label !text-[9px]">Lifecycle</th>
                    <th className="px-4 py-2 label !text-[9px]">NP Reason</th>
                    <th className="px-4 py-2 label !text-[9px]">Unit / Property</th>
                    <th className="px-4 py-2 label !text-[9px]">Developer / Operator</th>
                    <th className="px-4 py-2 label !text-[9px]">Panel / Inverter</th>
                    <th className="px-4 py-2 label !text-[9px] text-right">kWp</th>
                    <th className="px-4 py-2 label !text-[9px] text-right">CAPEX</th>
                    <th className="px-4 py-2 label !text-[9px] text-right">£/kWp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--line-soft,#EEE)]">
                  {result.rows.map(r => {
                    const cls = CLASSIFICATIONS.find(c => c.key === resolveClass(r.business_classification));
                    return (
                      <tr key={r.id} data-testid={`cr-row-${r.id}`}>
                        <td className="px-4 py-2 font-medium">
                          {r.name}
                          <div className="text-[11px] muted">{r.customer_name}</div>
                        </td>
                        <td className="px-4 py-2 text-xs" style={{ color: cls?.tone }}>{cls?.label || r.business_classification}</td>
                        <td className="px-4 py-2"><RiskBadge value={r.risk_status} size="xs" /></td>
                        <td className="px-4 py-2 text-xs">
                          {stageCatalog.find(s => s.key === r.lifecycle_status)?.label || r.lifecycle_status}
                        </td>
                        <td className="px-4 py-2 text-xs" style={{ maxWidth: 200 }}>
                          {r.not_proceeding_reason
                            ? <span className="text-[11px] muted" title={r.not_proceeding_reason}>{r.not_proceeding_reason}</span>
                            : <span className="muted">—</span>}
                        </td>
                        <td className="px-4 py-2 text-xs font-mono">
                          {r.unit_code || <span className="muted">—</span>}{" / "}
                          {r.property_code || <span className="muted">—</span>}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {r.developer || <span className="muted">—</span>}
                          <div className="text-[10px] muted">{r.operator || "—"}</div>
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {r.panel_manufacturer || <span className="muted">—</span>}
                          <div className="text-[10px] muted">{r.inverter_manufacturer || "—"}</div>
                        </td>
                        <td className="px-4 py-2 text-right font-mono" style={{ color: "var(--accent-hover)" }}>
                          {fmtKwp(r.system_size_kwp)}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">{currency(r.capex_gbp)}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs muted">
                          {currency(r.capex_per_kwp_effective)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!result && dashLoaded && (
        <div className="border border-dashed border-[var(--line)] rounded-lg bg-white p-8 text-center muted text-sm" data-testid="cr-empty-runstate">
          Choose filters above and hit <strong>Run report</strong> to see matching projects and their combined kWp + CAPEX.
        </div>
      )}

      {/* Save template dialog — native modal (not window.prompt) so mobile
          browsers + embedded webviews render correctly and we can add
          extras like the Share-with-team toggle. */}
      {saveDialog.open && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
          onClick={closeSaveDialog}
          data-testid="cr-save-dialog"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <Save size={14} style={{ color: "var(--accent-hover)" }} />
              <h3 className="font-display text-lg font-bold">
                {saveDialog.mode === "rename" ? "Rename report template" : "Save report template"}
              </h3>
            </div>
            <p className="muted text-xs mb-4">
              {saveDialog.mode === "rename"
                ? "Update the name (and share flag) so teammates always see the latest naming. Filters stay as-is."
                : "Templates capture the current filter set so you can rerun the same view later. Share with your team to hand a canonical board-pack view to colleagues."}
            </p>
            <label className="block mb-3">
              <div className="label !text-[10px] mb-1">Template name</div>
              <input
                className="input-swiss"
                value={saveDialog.name}
                onChange={(e) => setSaveDialog(d => ({ ...d, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") saveTemplate(); if (e.key === "Escape") closeSaveDialog(); }}
                placeholder="e.g. Q1 Board Pack — Retro"
                autoFocus
                data-testid="cr-save-name"
              />
            </label>
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={saveDialog.shared}
                onChange={(e) => setSaveDialog(d => ({ ...d, shared: e.target.checked }))}
                data-testid="cr-save-shared"
              />
              <span className="text-sm">Share with team</span>
              <span className="text-[10px] muted">— teammates can load but only you can edit/delete</span>
            </label>
            <div className="flex items-center justify-end gap-2">
              <button className="btn-ghost text-xs" onClick={closeSaveDialog} data-testid="cr-save-cancel">
                Cancel
              </button>
              <button className="btn-primary text-xs" onClick={saveTemplate} data-testid="cr-save-confirm">
                {saveDialog.mode === "rename" ? "Save changes" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm}
        title="Delete report template?"
        description={
          activeTemplateId
            ? `"${templates.find(t => t.id === activeTemplateId)?.name || ''}" will be permanently removed. Filters aren't affected — you can rebuild the template any time.`
            : ""
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        icon="trash"
        onConfirm={doDeleteTemplate}
        onCancel={() => setDeleteConfirm(false)}
      />
    </div>
  );
}


function ChipGroup({ label, options, selected, onToggle, testId }) {
  return (
    <div data-testid={testId}>
      <div className="label !text-[10px] mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.length === 0 ? (
          <span className="text-xs muted">No values yet — add via Project settings.</span>
        ) : options.map(opt => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onToggle(opt.value)}
              aria-pressed={active}
              className={`text-xs px-3 py-1 rounded border transition-all ${
                active ? "shadow-sm" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"
              }`}
              style={active ? { borderColor: opt.tone || "var(--accent-hover)",
                                background: `${opt.tone || "#314041"}15`,
                                color: opt.tone || "var(--accent-hover)" } : undefined}
              data-testid={`${testId}-${opt.value}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


function MultiSelectMini({ label, options, selected, onToggle, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid={testId}>
      <div className="label !text-[10px] mb-1">{label}</div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input-swiss w-full text-left flex items-center justify-between gap-2 cursor-pointer"
        data-testid={`${testId}-toggle`}
      >
        <span className="text-sm truncate">
          {selected.length === 0 ? <span className="muted">All ({options.length})</span> :
            selected.length === 1 ? selected[0] :
            `${selected.length} selected`}
        </span>
        <span className="text-xs muted">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 w-full max-h-64 overflow-auto border border-[var(--line)] rounded bg-white shadow-lg"
          data-testid={`${testId}-menu`}
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs muted">No values yet.</div>
          ) : options.map(opt => {
            const active = selected.includes(opt);
            return (
              <label
                key={opt}
                className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-[var(--surface-2,#F6F7F9)] cursor-pointer"
              >
                <input type="checkbox" checked={active} onChange={() => onToggle(opt)} className="cursor-pointer" />
                <span className="truncate">{opt}</span>
              </label>
            );
          })}
          <div className="border-t border-[var(--line)] px-3 py-1.5 text-right">
            <button type="button" className="text-[10px] muted hover:underline" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function SummaryCard({ label, value, accent, testId }) {
  return (
    <div className="border border-[var(--line)] rounded-lg bg-white p-4" data-testid={testId}>
      <div className="label !text-[9px] mb-1">{label}</div>
      <div
        className="font-display text-2xl font-bold leading-tight"
        style={{ color: accent ? "var(--accent-hover)" : "inherit" }}
      >
        {value}
      </div>
    </div>
  );
}
